import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Divider, Stack } from "@mui/material";

import { DataLayout, FieldControl, FileUpload } from "../../../shared/components";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useGetUsers } from "../../../shared/hooks";
import { useFetchTaskDetails } from "../hooks/useFetchTaskDetails";
import { useAddCheckList, useEditCheckList } from "../../stageGates/mutation";
import { FileUploadRef } from "../../../shared/components/FileUpload";
import { AutocompleteOption, ControlledAutocomplete } from "../../../shared/components/ControlledAutocomplete";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { Option } from "../../../shared/components/FieldControl";

export const RaiseTask: React.FC<{ isEdit: boolean, taskId: string | null; onClose: () => void; refetch: () => void; object_id?: string | null; object_type?: 'task' | 'meeting'; enabledFields?: string[]; disableFields?: string[]; meetingProjects?: Option[], meetingProgrammes?: Option[] }> = ({ isEdit, taskId, onClose, refetch, object_id, object_type, enabledFields, disableFields, meetingProjects, meetingProgrammes }) => {

    const addCheckList = useAddCheckList();
    const editCheckList = useEditCheckList();
    const fileUploadRef = useRef<FileUploadRef | null>(null);
    const { removeCache } = useFetchTaskDetails({ taskId: taskId || '' });
    const phaseData = usePhase();

    const { getFilterOptions } = useDisplayField([]);
    const { taskDetails, isPending } = useFetchTaskDetails({ taskId: taskId || '' });

    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();
    const userOptions = useMemo(() => {
        if (!usersData?.data) return [];
        return usersData.data
            .filter((u) => u.is_active === "1")
            .map((u) => ({
                id: u.user_id,
                name: `${u.first_name} ${u.last_name}`,
            }));
    }, [usersData]);

    const [task, setTask] = useState<{
        type: 'Project' | 'Portfolio' | 'Programme',
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
        title: null | string,
        description: null | string,
        status: null | string,
        priority: null | string,
        assigned_to: null | number,
        target_date: null | string,
        phase: null | string,
        actionCategory: null | string,
        tags: string[] | [],
    }>({
        type: 'Project',
        title: null,
        description: null,
        status: null,
        priority: null,
        assigned_to: null,
        target_date: null,
        project: null,
        portfolio: null,
        programme: null,
        phase: null,
        actionCategory: null,
        tags: [],
    });

    const onChange = (key: string, value: string | AutocompleteOption | AutocompleteOption[] | null) => {
        setTask({
            ...task,
            [key]: value
        });
    };

    const params = () => {
        const payload: any = {
            task_name: task.title,
            user_id: task.assigned_to,
            object_type: object_type || 'task',
            is_evidence_required: false,
            is_required: false,
            description: task.description,
            tags: task.tags,
            priority: task.priority,
            status: task.status,
            target_date: task.target_date,
            type: task.type,
            category: task.actionCategory,
        };
        if (task.type === 'Project' && task.project) {
            payload.project_id = task.project.id || task.project;
        };
        if (task.type === 'Portfolio' && task.portfolio) {
            payload.portfolio_id = task.portfolio.id || task.portfolio;
        };
        if (task.type === 'Programme' && task.programme) {
            payload.programme_id = task.programme.id || task.programme;
        };
        if (task.phase) {
            payload.project_tree_id = task.phase;
        };
        if (object_id) {
            payload.object_id = Number(object_id);
        };
        return payload;
    };

    const onSave = async () => {
        if (isEdit && taskId) {
            await editCheckList.mutateAsync({
                payload: params(),
                checkListID: taskId
            });
            if (fileUploadRef.current && taskId) {
                fileUploadRef.current.setId(taskId);
                setTimeout(() => {
                    fileUploadRef.current?.uploadAllFiles();
                }, 0);
            };
        } else {
            const response = await addCheckList.mutateAsync({
                payload: params()
            });
            if (fileUploadRef.current && response.data?.task_id) {
                fileUploadRef.current.setId(response.data?.task_id);
                setTimeout(() => {
                    fileUploadRef.current?.uploadAllFiles();
                }, 0);
            };
        };
    };

    useEffect(() => {
        if (isEdit && taskId) {
            if (taskDetails) {
                setTask({
                    title: taskDetails.task_name,
                    description: taskDetails.description,
                    tags: taskDetails.tags,
                    status: taskDetails.status,
                    priority: taskDetails.priority,
                    assigned_to: taskDetails.user_id,
                    target_date: taskDetails.target_date,
                    type: taskDetails.type,
                    project: taskDetails.project_id ? { id: taskDetails.project_id, label: taskDetails.project_title } : null,
                    portfolio: taskDetails.portfolio_id ? { id: taskDetails.portfolio_id, label: taskDetails.portfolio_title } : null,
                    programme: taskDetails.programme_id ? { id: taskDetails.programme_id, label: taskDetails.programme_title } : null,
                    phase: taskDetails.project_tree_id,
                    actionCategory: taskDetails.category,
                });
            }
        }
    }, [isEdit, taskId, taskDetails]);

    const activeProjectId = useMemo(() => {
        return task.project?.id;
    }, [task.project?.id]);

    useEffect(() => {
        if (activeProjectId)
            phaseData.mutateAsync(activeProjectId);
    }, [activeProjectId])

    if (isPending && isEdit) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'} overflow={'hidden'}>
            <Box flex={1} sx={{ overflowY: 'auto' }} p={2}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>

                    {
                        !disableFields?.includes('type') &&
                        <FieldControl
                            keyName={'type'}
                            label={"Type"}
                            variant={"select"}
                            options={[
                                { label: 'Project', value: 'Project' },
                                { label: 'Portfolio', value: 'Portfolio' },
                                { label: 'Programme', value: 'Programme' },
                            ]}
                            value={task.type}
                            onChange={onChange}
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Project' &&
                        <ControlledAutocomplete
                            type="PROJECT"
                            value={task.project}
                            onSelect={(val) => onChange('project', val)}
                            label="Search project"
                            placeholder="Search project"
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Project' &&
                        <FieldControl
                            keyName={'phase'}
                            label={"Select Project Phase"}
                            variant={"select"}
                            value={task.phase}
                            options={
                                phaseData.data?.data?.map((value: { id: string, name: string; }) => {
                                    return {
                                        value: value.id, label: value.name
                                    }
                                })
                            }
                            onChange={onChange}
                            loading={phaseData.isPending}
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Portfolio' &&
                        <ControlledAutocomplete
                            type="PORTFOLIO"
                            value={task.portfolio}
                            onSelect={(val) => onChange('portfolio', val)}
                            label="Search portfolio"
                            placeholder="Search portfolio"
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Programme' &&
                        <ControlledAutocomplete
                            type="PROGRAMME"
                            value={task.programme}
                            onSelect={(val) => onChange('programme', val)}
                            label="Search programme"
                            placeholder="Search programme"
                        />
                    }

                    {
                        !disableFields?.includes('type') &&
                        <Divider />
                    }

                    {
                        enabledFields && enabledFields.includes('meeting_project') &&
                        <FieldControl
                            keyName={"project"}
                            label={"Project"}
                            variant={"select"}
                            value={task.project}
                            options={meetingProjects || []}
                            onChange={(_key: string, value: any) => {
                                setTask({
                                    ...task,
                                    type: 'Project',
                                    project: value,
                                });
                            }}
                        />
                    }

                    {
                        enabledFields && enabledFields.includes('meeting_programme') &&
                        <FieldControl
                            keyName={"programme"}
                            label={"Programme"}
                            variant={"select"}
                            value={task.programme}
                            options={meetingProgrammes || []}
                            onChange={(_key: string, value: any) => {
                                setTask({
                                    ...task,
                                    type: 'Programme',
                                    programme: value,
                                });
                            }}
                        />
                    }

                    <FieldControl
                        keyName={"title"}
                        label={"Task Title"}
                        variant={"input"}
                        value={task.title}
                        onChange={onChange}
                    />

                    <FieldControl
                        keyName={"description"}
                        label={"Description"}
                        variant={"input"}
                        value={task.description}
                        onChange={onChange}
                    />

                    {
                        enabledFields && enabledFields.includes('action_category') &&
                        <FieldControl
                            keyName={"actionCategory"}
                            label={"Action Category"}
                            value={task.actionCategory}
                            variant={"select"}
                            options={getFilterOptions('MEETING_ACTION_CATEGORY').map((value) => {
                                return {
                                    label: value.display_text,
                                    value: value.key
                                }
                            })}
                            onChange={onChange}
                        />
                    }

                    {
                        enabledFields && enabledFields.includes('task_category') &&
                        <FieldControl
                            keyName={"actionCategory"}
                            label={"Task Category"}
                            value={task.actionCategory}
                            variant={"select"}
                            options={getFilterOptions('TASK_CATEGORY').map((value) => {
                                return {
                                    label: value.display_text,
                                    value: value.key
                                }
                            })}
                            onChange={onChange}
                        />
                    }

                    {
                        disableFields && !disableFields.includes('tags') &&
                        <FieldControl
                            keyName={"tags"}
                            label={"Tags"}
                            value={task.tags}
                            variant={"select"}
                            options={getFilterOptions('TASK_TAG').map((value) => {
                                return {
                                    label: value.display_text,
                                    value: value.key
                                }
                            })}
                            onChange={onChange}
                            multiSelect
                        />
                    }

                    <FieldControl
                        keyName={"status"}
                        label={"Status"}
                        value={task.status}
                        variant={"select"}
                        options={getFilterOptions('TASK_STATUS').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        onChange={onChange}
                    />

                    <FieldControl
                        keyName={"priority"}
                        label={"Priority"}
                        variant={"select"}
                        value={task.priority}
                        options={getFilterOptions('TASK_PRIORITY').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        onChange={onChange}
                    />

                    <FieldControl
                        keyName={'assigned_to'}
                        label={"Assigned to"}
                        variant={"select"}
                        options={userOptions.map((u) => ({
                            value: u.id,
                            label: u.name,
                        }))}
                        loading={isLoadingUsers}
                        value={task.assigned_to}
                        onChange={onChange}
                    />

                    <FieldControl
                        keyName={"target_date"}
                        label={"Target Date"}
                        variant={"datetime"}
                        value={task.target_date}
                        onChange={onChange}
                    />

                    <DataLayout
                        title="Attachments"
                        children={
                            <Box p={2}>
                                <FileUpload type={"tasks"} typeId={taskId} ref={fileUploadRef} onUploadComplete={() => {
                                    removeCache();
                                    onClose();
                                    refetch();
                                }} />
                            </Box>
                        }
                    />

                </Box>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button variant="outlined" loading={addCheckList.isPending || editCheckList.isPending} disabled={addCheckList.isPending || editCheckList.isPending} onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={onSave} loading={addCheckList.isPending || editCheckList.isPending} disabled={addCheckList.isPending || editCheckList.isPending}>Save</Button>
            </Stack>
        </Box>
    )

}